import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { humanizeNumber } from '../utils/format';
import { getFlagEmoji } from '../utils/countryFlags';
import { City } from '../types';
import { getCityImage } from '../utils/cityImages';

interface Props {
  city: City;
  position: 'left' | 'right';
  showPopulation: boolean;
  populationValue?: number;
  onChoose: (position: 'left' | 'right') => void;
  disabled?: boolean;
  isWinner?: boolean;
}

export function CityPanel({
  city,
  position,
  showPopulation,
  populationValue,
  onChoose,
  disabled,
  isWinner,
}: Props) {
  const [image, setImage] = useState<string | undefined>();

  useEffect(() => {
    let isActive = true;
    setImage(undefined);
    void getCityImage(city).then((result) => {
      if (isActive) setImage(result);
    });
    return () => {
      isActive = false;
    };
  }, [city.id, city.image_url, city.lat, city.lon]);
  const cityPhotoStyle = image ? ({ '--city-photo': `url("${image}")` } as CSSProperties) : undefined;

  const wikiHref = city.wikipedia_title
    ? `https://en.wikipedia.org/wiki/${encodeURIComponent(city.wikipedia_title)}`
    : null;

  return (
    <article className="city-panel" aria-label={`${position} city`} style={cityPhotoStyle}>
      <div className="city-visual" />
      <div className="city-overlay" />
      <div className="city-body">
        <div className="city-name-card">
          <p className="city-country">
            <span aria-hidden>{getFlagEmoji(city.country)}</span>
            <span>{city.country}</span>
          </p>
          <h2 className="city-name">{city.name}</h2>
        </div>

        {showPopulation ? (
          <div className={`population-card${isWinner ? ' is-winner' : ''}`}>
            <span className="population-label">Population</span>
            <span className="population-value">{humanizeNumber(populationValue ?? city.population)}</span>
          </div>
        ) : (
          <div aria-hidden className="population-placeholder" />
        )}

        <div className="primary-action">
          <button
            className="btn primary icon-btn"
            onClick={() => onChoose(position)}
            disabled={disabled}
            aria-label={`Choose ${city.name} as bigger city`}
          >
            <svg aria-hidden viewBox="0 0 24 24" focusable="false">
              <path fill="currentColor" d="M12 4l6 6h-4v10h-4V10H6l6-6z" />
            </svg>
            <span>Bigger</span>
          </button>
        </div>
        {wikiHref ? (
          <a className="btn icon-btn wiki-btn" href={wikiHref} target="_blank" rel="noreferrer">
            <svg aria-hidden viewBox="0 0 24 24" focusable="false">
              <path
                fill="currentColor"
                d="M5 4h14a1 1 0 011 1v3h-2V6H6v12h4v2H5a1 1 0 01-1-1V5a1 1 0 011-1zm9 5h2v3h3v2h-3v3h-2v-3h-3v-2h3V9z"
              />
            </svg>
            <span>Wiki</span>
          </a>
        ) : null}
      </div>
    </article>
  );
}
